import { MenuTabItem } from "~/entities/menuEntities";
import { ResponseLine, ResponsePromotion } from "~/entities/orderEntities";
import { getPromoSelectionsRequired } from "~/helpers/orderHelper";

// What an offer asks the customer to choose, as builder-style slots: the items it needs first (paid), then the items
// it discounts. Amounts worked out from these are estimates for display; the server prices the cart.

export type OfferDiscount = { percentTimes1000: number; fixedAmount: number };

export type InCartUnits = {
  productCode: string;
  name: string;
  image: MenuTabItem["productData"]["image"];
  qty: number;
};

export type PickerSlot = {
  key: string;
  // Band label: the item's name when there's only one, otherwise "Items" / "Your free item"
  label: string;
  // Every unit chosen here gets the offer's discount
  discounted: boolean;
  // Units the slot needs in all, counting those already in the order
  quantity: number;
  // Items already in the order that count toward this slot
  inCart: InCartUnits[];
  items: MenuTabItem[];
  // How many of the units chosen here the offer discounts, when only some are ("2nd one free")
  discountedUnits?: (picked: number) => number;
};

export type OfferPick = {
  offerName: string;
  // Wording from the reward itself (Como), which replaces the generated explanation
  text?: string;
  slots: PickerSlot[];
  discount: OfferDiscount | null;
  // Buy N get one discounted, all the same items: "Your second Dog is free."
  nthDiscounted?: number;
};

export const isFreeDiscount = (discount: OfferDiscount | null) =>
  (discount?.percentTimes1000 ?? 0) >= 100000;

// A unit's price after the offer's discount
export const discountedPrice = (price: number, discount: OfferDiscount) =>
  discount.percentTimes1000 > 0
    ? Math.round((price * (100000 - discount.percentTimes1000)) / 100000)
    : Math.max(0, price - discount.fixedAmount);

export const ordinal = (n: number) => {
  const words = ["", "first", "second", "third", "fourth", "fifth"];
  return words[n] ?? `${n}th`;
};

// "free", "25% off", "$5.00 off"
export const discountWords = (
  discount: OfferDiscount,
  formatAmount: (x: number) => string | null | undefined,
) =>
  isFreeDiscount(discount)
    ? "free"
    : discount.percentTimes1000 > 0
      ? `${discount.percentTimes1000 / 1000}% off`
      : `${formatAmount(discount.fixedAmount)} off`;

const unitsInCart = (
  lines: ResponseLine[],
  codes: string[],
  products: MenuTabItem[],
): InCartUnits[] => {
  const byCode: Record<string, InCartUnits> = {};
  for (const line of lines) {
    const code = line.item.productCode;
    if (!codes.includes(code)) {
      continue;
    }
    const menuItem = products.find((x) => x.productCode === code);
    byCode[code] ??= {
      productCode: code,
      name: menuItem?.productData.name ?? line.item.displayName,
      image: menuItem?.productData.image ?? null,
      qty: 0,
    };
    byCode[code].qty += line.item.qty;
  }
  return Object.values(byCode);
};

// Units of the offer's items it would discount with n of them in the order
const discountedFor = (promotion: ResponsePromotion, n: number) => {
  const requiredQty = Math.max(1, promotion.requiredQty);
  let allowed =
    promotion.maxUsesPerRequirements == null
      ? n >= requiredQty
        ? n
        : 0
      : promotion.maxUsesPerRequirements * Math.floor(n / requiredQty);
  if (promotion.maxUsesPerOrder != null) {
    allowed = Math.min(allowed, promotion.maxUsesPerOrder);
  }
  return Math.min(n, allowed);
};

const itemsFor = (products: MenuTabItem[], codes: string[]) =>
  products.filter((x) => codes.includes(x.productCode));

// The choice a promotion from a code still needs, or null when it needs nothing the customer can pick
export const offerPickForPromotion = (
  promotion: ResponsePromotion,
  lines: ResponseLine[],
  products: MenuTabItem[],
  offerName = promotion.name,
): OfferPick | null => {
  const { requiredQtyRemaining, rewardQtyRemaining } =
    getPromoSelectionsRequired(lines, promotion);
  const requiredCodes = promotion.requiredProductCodes ?? [];
  const rewardCodes = promotion.productCodes ?? [];
  const discount = {
    percentTimes1000: promotion.percentTimes1000,
    fixedAmount: promotion.fixedAmount,
  };
  const requiredQty = Math.max(1, promotion.requiredQty);

  // The discounted items are among the ones it needs (buy one get one, a free Dog): one slot, some units discounted
  const sameItems =
    !!rewardCodes.length && rewardCodes.every((x) => requiredCodes.includes(x));
  if (sameItems) {
    if (!requiredQtyRemaining) {
      return null;
    }
    const inCart = unitsInCart(lines, requiredCodes, products);
    const inCartQty = inCart.reduce((acc, x) => acc + x.qty, 0);
    const alreadyDiscounted = discountedFor(promotion, inCartQty);
    const discountedUnits = (picked: number) =>
      Math.max(
        0,
        Math.min(
          picked,
          discountedFor(promotion, inCartQty + picked) - alreadyDiscounted,
        ),
      );
    const items = itemsFor(products, requiredCodes);
    const single = items.length === 1 ? items[0].productData.name : null;
    // Only one more is needed and it's the discounted one: choosing the free item
    const justTheReward =
      requiredQtyRemaining === 1 && discountedUnits(1) === 1;
    return {
      offerName,
      discount,
      nthDiscounted: requiredQty >= 2 ? requiredQty : undefined,
      slots: [
        justTheReward
          ? {
              key: "reward",
              label:
                single ?? `Your ${isFreeDiscount(discount) ? "free " : ""}item`,
              discounted: true,
              quantity: 1,
              inCart: [],
              items,
            }
          : {
              key: "required",
              label: single ?? "Items",
              discounted: false,
              quantity: inCartQty + requiredQtyRemaining,
              inCart,
              items,
              discountedUnits,
            },
      ],
    };
  }

  const slots: PickerSlot[] = [];
  if (requiredQtyRemaining && requiredCodes.length) {
    const items = itemsFor(products, requiredCodes);
    const inCart = unitsInCart(lines, requiredCodes, products);
    slots.push({
      key: "required",
      label: items.length === 1 ? items[0].productData.name : "Items",
      discounted: false,
      quantity: requiredQty,
      inCart,
      items,
    });
  }
  if (rewardCodes.length && (rewardQtyRemaining || requiredQtyRemaining)) {
    const items = itemsFor(products, rewardCodes);
    const free = isFreeDiscount(discount);
    slots.push({
      key: "reward",
      label: `Your ${free ? "free " : ""}${items.length === 1 ? items[0].productData.name : "item"}`,
      discounted: true,
      quantity: Math.max(1, rewardQtyRemaining),
      inCart: [],
      items,
    });
  }
  return slots.length ? { offerName, discount, slots } : null;
};
